/**
 * Der Zugang zur Schnittstelle.
 *
 * Die Oberfläche ist ein Verbraucher der API, kein Sonderweg — sie ruft
 * dieselben Prozeduren auf, die auch Metarr und ein verbundener MCP-Client
 * bekommen.
 */

import { createTRPCClient, httpBatchLink, TRPCClientError } from "@trpc/client";
import type { AppRouter } from "@cubic/server/src/trpc/router.js";
import {
  anmeldungDrehtSichImKreis,
  melde_ab,
  melde_an,
  token,
  vergissAnmeldeversuch,
} from "./auth.js";

const adresse =
  import.meta.env["VITE_API_URL"] ?? "http://localhost:3001/trpc";


/**
 * Die MCP-Adresse dieses Servers.
 *
 * Abgeleitet aus der API-Adresse, die der Client ohnehin benutzt — der MCP-
 * Endpunkt liegt am selben Ursprung unter /mcp. Damit steht in der
 * Konfiguration, die ein Mensch kopiert, garantiert derselbe Server, mit dem
 * die Oberfläche gerade spricht. Ein Eingabefeld dafür wäre eine Fehlerquelle
 * ohne Gewinn.
 */
export function mcpAdresse(): string {
  return adresse.replace(/\/trpc\/?$/, "") + "/mcp";
}

let umleitungLaeuft = false;

function beiUnauthorized(): void {
  if (umleitungLaeuft) return;

  /*
   * Die eigentliche Bremse. `umleitungLaeuft` hilft nur innerhalb eines
   * Seitenlebens — und eine Runde durch Authentik ist ein Seitenneuaufbau,
   * nach dem die Variable wieder `false` ist. Kommt der 401 kurz nach einem
   * Anmeldeversuch zurück, dann hat die Anmeldung selbst nichts geändert.
   * Eine weitere hilft dann auch nicht; sie flackert nur.
   *
   * Statt dessen zurück auf die Anmeldeseite, wo ein Mensch entscheidet.
   */
  if (anmeldungDrehtSichImKreis()) {
    umleitungLaeuft = true;
    vergissAnmeldeversuch();
    melde_ab();
    return;
  }

  umleitungLaeuft = true;
  const pfad =
    globalThis.location.pathname + globalThis.location.search || "/";
  void melde_an(pfad);
}

export const api = createTRPCClient<AppRouter>({
  links: [
    httpBatchLink({
      url: adresse,
      headers() {
        const wert = token();
        return wert ? { authorization: `Bearer ${wert}` } : {};
      },
      fetch(url, options) {
        return globalThis.fetch(url, options).then((antwort) => {
          if (antwort.status === 401) beiUnauthorized();
          // Ein durchgekommener Aufruf beweist, dass die Anmeldung trägt —
          // erst dann ist der Verdacht auf eine Schleife wieder vom Tisch.
          else if (antwort.ok) vergissAnmeldeversuch();
          return antwort;
        });
      },
    }),
  ],
});

/**
 * Der maschinenlesbare Grund einer Abweisung.
 */
export function grundVon(fehler: unknown): string | null {
  if (fehler instanceof TRPCClientError) {
    const daten = fehler.data as
      | { grund?: string | null; httpStatus?: number; code?: string }
      | undefined;
    if (daten?.httpStatus === 401 || daten?.code === "UNAUTHORIZED") {
      return "401";
    }
    return daten?.grund ?? null;
  }
  return null;
}

/** Der Satz, den der Server zur Abweisung mitgeschickt hat. */
export function hinweisVon(fehler: unknown): string | null {
  if (fehler instanceof TRPCClientError) {
    const daten = fehler.data as { hinweis?: string | null } | undefined;
    const text = daten?.hinweis?.trim();
    return text ? text : null;
  }
  return null;
}

export function istNetzfehler(fehler: unknown): boolean {
  if (fehler instanceof TRPCClientError) {
    return (
      fehler.message.includes("Failed to fetch") ||
      fehler.message.includes("NetworkError") ||
      fehler.message.includes("fetch failed")
    );
  }
  return fehler instanceof TypeError;
}
